import { A } from '@solidjs/router';
import { For, type JSX, Match, Switch } from 'solid-js';
import { t } from '../i18n';
import { byTaskRecency, type TaskRow } from '../lib/fleet';
import { taskWorkWord } from '../lib/fleet-work';
import type { UtilBox } from '../lib/fleetUtilization';
import { RECENT_WORK_ROWS, useMachineRecentWork } from '../lib/machine-panel-reads';
import { orgHref } from '../lib/org';
import { ago } from '../lib/time';
import { Skeleton } from './Skeleton';
import { StatusPill } from './StatusPill';
import { TASK_STATUS_TONE } from './TaskQueueItem';

// The details panel's Recent work (#6565, prototype `panel()`): the machine's five newest
// tasks, server-filtered (`GET /v1/tasks?filter=runner_id:eq:<id>`, #6558), each its
// title, repo, the board's work word (`taskWorkWord`) and when it was created, then
// "All work on <name>" into `/fleet/work?machine=<id>` (#6788), which keeps every control
// the queue has.
//
// NO DURATION. The prototype's "24 min" is sample data: the task list row carries
// `created_at` and no start, finish or duration (`TaskWireSchema`), so the time shown is
// when the task was created. An intentional deviation (#6565).
//
// The caller renders this for the org's OWN machines only (G23): the list is
// account-scoped, so another org's machine would read as an honest-looking "no work".

export function MachineRecentWork(props: {
  readonly box: UtilBox;
  readonly token: string | null;
}): JSX.Element {
  const work = useMachineRecentWork(() => props.box.id, props.token);
  const rows = (): readonly TaskRow[] =>
    [...(work.data?.data ?? [])].sort(byTaskRecency).slice(0, RECENT_WORK_ROWS);
  return (
    <section class="machine-panel__section" aria-labelledby="machine-panel-work">
      <div class="machine-panel__res-head">
        <h3 id="machine-panel-work" class="machine-panel__h3">
          {t('fleet.panel.recent_work')}
        </h3>
        <A href={orgHref(`/fleet/work?machine=${encodeURIComponent(props.box.id)}`)}>
          {t('fleet.panel.all_work_on', { name: props.box.display_name })}
        </A>
      </div>
      <Switch>
        <Match when={work.data !== undefined && rows().length === 0}>
          <p class="machine-panel__note">{t('fleet.panel.no_work')}</p>
        </Match>
        <Match when={work.data !== undefined}>
          <ul class="machine-panel__work">
            <For each={rows()}>
              {(task) => (
                <li class="machine-panel__work-row" data-status={task.status}>
                  <span class="machine-panel__work-title">{task.title}</span>
                  <StatusPill tone={TASK_STATUS_TONE[task.status]}>
                    {t(taskWorkWord(task))}
                  </StatusPill>
                  <span class="machine-panel__work-repo">{task.repo_full_name}</span>
                  <span class="machine-panel__work-time">{ago(task.created_at)}</span>
                </li>
              )}
            </For>
          </ul>
        </Match>
        <Match when={work.isError}>
          <p class="machine-panel__note" role="status">
            {t('fleet.panel.work_failed')}
          </p>
        </Match>
        <Match when={true}>
          <div role="status">
            <span class="sr-only">{t('states.loading')}</span>
            <Skeleton height="6rem" />
          </div>
        </Match>
      </Switch>
    </section>
  );
}
